import React, {useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import {graphql} from 'react-relay';
import {TimesheetsPage} from './TimesheetsPage';
import Util from '../../shared/util/util';
import ForecastQueryRenderer from '../../../ForecastQueryRenderer';

const timesheetsPageQuery = graphql`
	query TimesheetsPageQueryWrapper_Query(
		$personId: ID
		$startDateString: String
		$endDateString: String
		$fetchCustomFields: Boolean!
	) {
		viewer {
			actualPersonId
			component(name: "timesheet")
			...TimesheetsPage_viewer
				@arguments(
					personId: $personId
					startDateString: $startDateString
					endDateString: $endDateString
					fetchCustomFields: $fetchCustomFields
				)
		}
	}
`;

// This is a bit hacky - we removed the imperitive re-fetches from Timesheets page, but
// it can't yet handle the empty state between renders. This persists data until
// new data is recieved. Improve this when you can.
function CachedTimesheetsPage(props) {
	const [cachedViewer, setCachedViewer] = useState(props.viewer);
	useEffect(() => {
		if (props.viewer) setCachedViewer(props.viewer);
	}, [props.viewer]);

	return <TimesheetsPage {...props} viewer={props.viewer ?? cachedViewer} />;
}

export function TimesheetsPageQueryWrapper({actualPersonId}) {
	const [selectedPersonId, setSelectedPersonId] = useState(actualPersonId);

	const [currentViewingDate, setCurrentViewingDate] = useState(() => moment().startOf('day'));
	// The month is stored separately, rather than as a memo on currentViewingDate, as the date
	// may be outside the current month - e.g. the 31st of Jan is selectable from the Feb view.
	const [currentViewingMonth, setCurrentViewingMonth] = useState(currentViewingDate);

	const startDateString = currentViewingMonth.clone().startOf('month').startOf('week').format('YYYY-MM-DD');
	const endDateString = currentViewingMonth.clone().endOf('month').endOf('week').format('YYYY-MM-DD');
	const fetchCustomFields = Util.hasCustomFields();

	const variables = useMemo(
		() => ({
			personId: selectedPersonId,
			startDateString,
			endDateString,
			fetchCustomFields,
		}),
		[selectedPersonId, startDateString, endDateString, fetchCustomFields]
	);

	return (
		<ForecastQueryRenderer
			key="query-render-timesheetsPage"
			query={timesheetsPageQuery}
			variables={variables}
			alwaysRender
			render={(relayProps, retry) => (
				<CachedTimesheetsPage
					retry={retry}
					{...relayProps}
					selectedPersonId={selectedPersonId}
					setSelectedPersonId={setSelectedPersonId}
					currentViewingDate={currentViewingDate}
					setCurrentViewingDate={setCurrentViewingDate}
					currentViewingMonth={currentViewingMonth}
					setCurrentViewingMonth={setCurrentViewingMonth}
				/>
			)}
		/>
	);
}
